/**
 * The arrangements the wiki's cards can stand in.
 * ---------------------------------------------------------------------------
 * Adapted from three.js's `css3d_periodictable` example, which lays 118
 * elements out as a table, a sphere, a helix and a grid and tweens between
 * them. Three of those four are here - the helix was dropped. Everything below
 * is the geometry half of that example, rewritten against plain numbers:
 * three.js is not used, and neither is a renderer — each layout answers, per
 * card, a position and a rotation, and the stage writes those into a CSS
 * `transform`.
 *
 * Two departures from the example, both because our card is not its tile:
 *
 *   - the example's spacings are constants (140 and 180, for a 120x160 tile).
 *     Ours are derived from the card's own measure, so the arrangements hold
 *     their proportions if the card is ever resized.
 *   - the example's table is the periodic table, whose columns mean something.
 *     Ours has nothing to say, so its rows and columns are solved from the
 *     stage's own proportions, which is what makes TABLE read as the plain
 *     shelf the page had before, and the other three as departures from it.
 *
 * A layout returns, for every index: x, y, z in pixels and rx, ry, rz in
 * radians - where the card stands, and which way it faces when it is left to
 * the arrangement. The stage applies them as
 * `translate3d(x, y, z) rotateY(ry) rotateX(rx) rotateZ(rz)`, so a card's face
 * (its own +Z) ends up pointing along
 * `(cos rx · sin ry, −sin rx, cos rx · cos ry)` — which is what the `lookAt`
 * calls in the example work out to, and how the sphere's angles below are
 * derived.
 *
 * These are the facings the arrangement itself wants: the sphere's cards look
 * outwards, as the periodic table's tiles do. Follow mode overrides them at
 * render time and turns every card to the middle of the screen instead (see
 * useForgeWikiStage); with it off, this is what is drawn.
 * ---------------------------------------------------------------------------
 */

/** The card's own box. Kept in step with .forge-wiki-card in ForgeWiki.css,
 *  which is what every spacing below is measured from: a height here that is
 *  shorter than the card really is spaces the rows to overlap. */
export const CARD_W = 280;
export const CARD_H = 124;

/* The corner.
   -------------------------------------------------------------------------
   One number, because three things have to agree on it: the radius CSS gives
   .forge-wiki-card, the rounded rectangle the GPU draws its faces inside, and
   the outline it extrudes those faces from. They are the same card seen three
   ways - and the third is the one that made this a constant, because a box
   with square corners behind a face with round ones shows four little tabs
   sticking out past the drawing. */
export const CARD_RADIUS = 12;

/**
 * The lens, and where the camera stands with it.
 * ---------------------------------------------------------------------------
 * `PERSPECTIVE` is the focal length CSS declares on the stage (kept in step
 * with `perspective` on .forge-wiki-stage__zoom). It is FIXED - nothing, no
 * turn and no zoom, changes it - and so is the camera's distance while a scene
 * is being looked at.
 *
 * What that distance IS depends on the arrangement, and that is not a
 * contradiction: it is where a photographer stands for this subject, decided
 * once when the subject changes. A sphere of a hundred and twenty cards is
 * some 3700 deep; stood too close to it, the near cards tower over the far
 * ones and the ball comes out as a funnel - which is what a wide-angle lens
 * does, and what the one fixed distance this briefly used produced. cameraFor
 * below stands the camera at least five times the arrangement's own depth
 * away - and reaches for a longer lens to make up the difference - which holds
 * the near-to-far ratio around 1.5 and the sphere looking like a sphere.
 *
 * Zoom never touches it. Magnification is a flat 2D scale outside the
 * perspective (see .forge-wiki-stage__zoom in ForgeWiki.css), which is also
 * what keeps the type sharp, and a turn about a card moves the pivot across
 * the camera's plane rather than along its axis.
 * ---------------------------------------------------------------------------
 */
export const PERSPECTIVE = 1000;

/* One card and the air around it, the example's 140/180 against its 120/160. */
const STEP_X = CARD_W * 1.12;
const STEP_Y = CARD_H * 1.18;

const at = (x, y, z, ry = 0, rx = 0, rz = 0) => ({ x, y, z, rx, ry, rz });

/**
 * The shelf: rows and columns, every card facing the camera.
 *
 * Columns come from the stage's width so the arrangement fills it rather than
 * standing in a column of its own, and the block is centred on both axes.
 */
export function tableLayout(count, { width = 1200, height = 700 } = {}) {
  // The column count that gives the block the stage's own proportions, rather
  // than as many columns as happen to fit: a block shaped like its viewport
  // leaves no bar of empty stage down one side or along the bottom, and fills
  // the same share of it whatever the window is doing.
  //
  //   cols · STEP_X     W                       n · STEP_Y · W
  //   -------------  =  -   solved for cols  =  sqrt( ------------- )
  //   rows · STEP_Y     H                         STEP_X · H
  //
  // with rows = n / cols.
  const ideal = Math.sqrt((count * STEP_Y * width) / (STEP_X * height));
  const cols = Math.max(1, Math.min(count, Math.round(ideal) || 1));
  const rows = Math.ceil(count / cols);
  const offsetX = ((cols - 1) * STEP_X) / 2;
  const offsetY = ((rows - 1) * STEP_Y) / 2;

  return Array.from({ length: count }, (_, i) => at(
    (i % cols) * STEP_X - offsetX,
    (Math.floor(i / cols)) * STEP_Y - offsetY,
    0,
  ));
}

/**
 * The sphere: cards spread evenly over its surface, each facing outwards.
 *
 * The example's own spiral — `phi = acos(-1 + 2i/n)`, `theta = sqrt(n·pi)·phi`
 * — which distributes points evenly rather than in visible rings. Its radius
 * is the one number that has to grow with the count, or thirty cards on a
 * ten-card sphere overlap; it is solved from the area each card needs.
 */
export function sphereLayout(count, { radius } = {}) {
  const n = Math.max(count, 1);
  // The example's own proportion, carried over: its 118 tiles of 120px stand
  // on a radius of 800, which is 6.67 tile-widths - and a sphere's radius goes
  // with the square root of how many things are on it. So
  // r = w · 6.67 · sqrt(n / 118), which is w · 0.61 · sqrt(n). Packing them
  // tighter than that is what turned the sphere into a pile.
  const r = radius || Math.max(480, CARD_W * 0.61 * Math.sqrt(n));

  return Array.from({ length: count }, (_, i) => {
    const phi = Math.acos(-1 + (2 * i) / n);
    const theta = Math.sqrt(n * Math.PI) * phi;

    // three.js's setFromSphericalCoords: phi from +Y, theta round it.
    const x = r * Math.sin(phi) * Math.sin(theta);
    const y = r * Math.cos(phi);
    const z = r * Math.sin(phi) * Math.cos(theta);

    // facing away from the centre - the example's object.lookAt(position · 2)
    return at(x, y, z, Math.atan2(x, z), -Math.asin(y / r));
  });
}

/**
 * The grid: a block of cards, five across, five down, and then another layer
 * behind it — the example's arrangement, at our spacings.
 */
export function gridLayout(count) {
  const perRow = 5;
  const perLayer = perRow * perRow;
  const stepX = CARD_W * 1.4;
  const stepY = CARD_H * 1.5;
  const stepZ = CARD_W * 2.6;
  const layers = Math.ceil(count / perLayer);
  const offsetZ = ((layers - 1) * stepZ) / 2;
  // Centred on the rows there actually are, not on the five a full layer would
  // have: with two rows of cards the block otherwise hangs in the bottom of the
  // stage with three rows of nothing above it.
  const rows = Math.min(perRow, Math.ceil(Math.min(count, perLayer) / perRow));
  const offsetY = ((rows - 1) * stepY) / 2;

  return Array.from({ length: count }, (_, i) => at(
    (i % perRow) * stepX - ((perRow - 1) * stepX) / 2,
    offsetY - (Math.floor(i / perRow) % perRow) * stepY,
    offsetZ - Math.floor(i / perLayer) * stepZ,
  ));
}

/**
 * The shelf: one card at the top-left corner and the rest in a column under it.
 *
 * Not one of the arrangements the picker offers - it is what a card becomes
 * when it is chosen, and the way to read down a set: the chosen one is at the
 * top and everything else hangs below it in order, wrapping round the end so
 * the column reads as a reel rather than as a list with a beginning and an end.
 *
 * The camera does the placing: it carries the column to the corner with its
 * own screen offset and brings the cards back to life size with the zoom (see
 * shelfCamera below). That is what makes "the top-left corner" mean the same
 * thing at every window size.
 */
export function shelfLayout(count, selected, { height = 700 } = {}) {
  /* Seven down the stage, and a pile after that.
     -----------------------------------------------------------------------
     The chosen card is whole at the top and the next few hang below it, spread
     to fill the height the stage actually has - seven slots, which is as many
     as can be told apart at a glance. Everything past the seventh goes onto the
     last slot, one on top of the other: a shelf of a hundred and twenty is a
     column of seven and a thick pile, not a mile of card.

     Each card below the head is laid OVER the one above it, so what shows of
     every one is its own top strip - the mark, the name and the repository.

     Painting order is a real 3D sort (the world is `preserve-3d`), so it
     follows depth: a fraction of a pixel nearer the camera per row puts a card
     in front of the one above. The fraction divides three pixels between
     however many cards there are, because the column sits well off the camera's
     axis where depth shifts the projection sideways - a fixed step per row
     drifted the column 16px across a hundred and twenty cards. */
  const SLOTS = 7;
  const lean = 3 / Math.max(count, 2);
  const at0 = ((selected % count) + count) % count;

  // the room under the head, shared between the slots that follow it
  const room = Math.max(120, height - 44 - CARD_H);
  const step = clamp(room / (SLOTS - 1), 44, CARD_H + 12);

  return Array.from({ length: count }, (_, i) => {
    const row = (i - at0 + count) % count;
    if (row === 0) return at(0, 0, 0);
    const slot = Math.min(row, SLOTS - 1);
    return at(0, CARD_H + 12 + (slot - 1) * step, row * lean);
  });
}

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v));
}

/**
 * Where the camera stands for the shelf: square on, life size, with the chosen
 * card in the top-left corner of the stage.
 *
 * The camera stands at exactly the focal length here, where the projection is
 * 1:1 - a column of cards is flat, so there is no depth to stand back from -
 * and the screen offset is then plain pixels: half the viewport, less the
 * margin and half the card, is the way from the middle to that corner.
 */
export function shelfCamera({ width, height }, margin = 22) {
  return {
    distance: PERSPECTIVE,
    zoom: 1,
    yaw: 0,
    pitch: 0,
    tx: 0, ty: 0, tz: 0,
    ox: -(width / 2) + margin + CARD_W / 2,
    oy: -(height / 2) + margin + CARD_H / 2,
  };
}

/** The arrangements, in the order the picker offers them. */
/* The keys are the geometry's own and do not change - every function below is
   written against them. What changes here is what they are CALLED: the first
   is a grid as anyone looking at it would say, and the third is the same idea
   carried into depth, which is the only thing that distinguishes them. */
export const WIKI_LAYOUTS = [
  { key: "table", label: "Grid" },
  { key: "sphere", label: "Sphere" },
  { key: "grid", label: "Multi dim grid" },
];

/**
 * Where each card stands in a given arrangement, and how far back the camera
 * has to be to see all of it. The distance is the arrangement's own size — a
 * sphere of thirty cards is bigger than a sphere of three, and the camera has
 * to know.
 */
export function layoutTargets(layout, count, { width = 1200, height = 700, shelf = -1 } = {}) {
  // A chosen card overrides the arrangement: the cards are a column at the
  // corner until it is let go again.
  if (shelf >= 0 && count > 0) {
    return {
      targets: shelfLayout(count, shelf, { height }),
      camera: shelfCamera({ width, height }),
    };
  }

  const targets =
    layout === "sphere" ? sphereLayout(count)
      : layout === "grid" ? gridLayout(count)
        : tableLayout(count, { width, height });

  return {
    targets,
    camera: { ...cameraFor(targets, { width, height }), tx: 0, ty: 0, tz: 0, ox: 0, oy: 0 },
  };
}

/**
 * Where to stand for an arrangement, and how much lens to use from there.
 *
 * Two numbers, because framing and perspective are two different questions and
 * one distance cannot answer both:
 *
 *   - `toFit` is the distance at which the arrangement's half-extent fills
 *     half the viewport. Stand there and it is framed, but a deep arrangement
 *     seen from that close is seen through a wide angle: the near cards tower
 *     over the far ones and a ball of cards comes out as a funnel.
 *   - so the camera stands at least five times the arrangement's own depth
 *     away, which holds the near-to-far ratio around 1.5 - and the zoom makes
 *     up exactly what that extra distance cost, since magnification here is a
 *     flat scale outside the perspective and changes nothing about the shape
 *     of the scene.
 *
 * Which is what a photographer does with a deep subject: step back, and reach
 * for a longer lens.
 *
 * A card's reach is taken from its corner rather than its edge: turned, its
 * footprint is somewhere between its width and its diagonal.
 */
function cameraFor(targets, { width, height }) {
  if (!targets.length) return { distance: PERSPECTIVE * 2, zoom: 1 };

  const R = Math.hypot(CARD_W, CARD_H) / 2;
  let maxX = 0;
  let maxY = 0;
  let maxZ = 0;
  targets.forEach(t => {
    maxX = Math.max(maxX, Math.abs(t.x) + R);
    maxY = Math.max(maxY, Math.abs(t.y) + R);
    maxZ = Math.max(maxZ, Math.abs(t.z));
  });

  // in frame: the half-extent filling half the viewport, plus the depth in
  // front of the middle so the nearest card is well clear of the lens, plus air
  const toFit = Math.max(
    (maxX * PERSPECTIVE) / (width / 2),
    (maxY * PERSPECTIVE) / (height / 2),
  ) + maxZ + 200;

  const distance = Math.max(toFit, maxZ * 5 + PERSPECTIVE * 0.3);

  // and the lens that gives back what standing further away took
  return { distance, zoom: distance / toFit };
}
